import { computed, reactive, watch } from 'vue';
import useUrlQuery from '@/composables/useUrlQuery';
import { useStore } from 'vuex';

const DEFAULT_PER_PAGE = 5;

export default function usePagination(options = {}) {
  const { per_page = DEFAULT_PER_PAGE, sync_url = false, url_state = null, onChange = null } = options;

  const store = useStore();
  const url_params = url_state || (sync_url ? useUrlQuery() : null);

  const pagination = reactive({
    page: (url_params && parseInt(url_params.page, 10)) || 1,
    per_page: (url_params && parseInt(url_params.per_page, 10)) || per_page,
    total_pages: 0,
    total_count: 0,
  });

  // sync pagination state with URL query params
  if (url_params) {
    watch(
      () => [pagination.page, pagination.per_page],
      ([page_val, per_page_val]) => {
        url_params.page = page_val > 1 ? String(page_val) : undefined;
        url_params.per_page = per_page_val !== per_page ? String(per_page_val) : undefined;
      },
    );
  }

  /**
   * Update pagination totals from FETCH_*_PAGE response.
   * Call after dispatching the page action:
   *   const result = await store.dispatch('FETCH_*_PAGE', { ...params.value });
   *   updateFromResponse(result.pagination);
   */
  function updateFromResponse(response_pagination) {
    if (!response_pagination) return;
    if (response_pagination.total_pages !== undefined) pagination.total_pages = response_pagination.total_pages;
    if (response_pagination.total_count !== undefined) pagination.total_count = response_pagination.total_count;
  }

  /**
   * Handle @change event from <Pagination> component.
   * Updates page and per_page from the emitted pagination object.
   */
  function handlePageChange(pagination_next) {
    pagination.page = pagination_next.page;
    pagination.per_page = pagination_next.per_page;
  }

  /**
   * Combined handler for <Pagination @change="onPageChange" />.
   * Updates page/per_page and calls the onChange callback if provided.
   */
  function onPageChange(pagination_next) {
    handlePageChange(pagination_next);
    if (onChange) onChange();
  }

  function resetPage() {
    pagination.page = 1;
  }

  // params ready to spread into API dispatch calls
  const params = computed(() => ({
    page: pagination.page,
    per_page: pagination.per_page,
  }));

  async function dispatchPage(action, extra_params = {}) {
    const result = await store.dispatch(action, { ...params.value, ...extra_params });
    updateFromResponse(result.pagination);
    return result;
  }

  return { pagination, params, updateFromResponse, handlePageChange, onPageChange, resetPage, dispatchPage };
}
